import React, { useEffect, useState } from 'react'
import { Box, Button, Popover, Typography } from '@mui/material'
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined'
import moment from 'moment/moment'
import { GetRequest } from '../common/api'
import { urls } from '../common/constants'

export const EMPTY_USER_COLLECTION = {
  totalBooking: 0,
  cashBooking: 0,
  qrBooking: 0,
  upiBooking: 0,
  otherBooking: 0,
  totalDelivery: 0,
  cashDelivery: 0,
  qrDelivery: 0,
  upiDelivery: 0,
  otherDelivery: 0,
  totalCollection: 0,
}

const formatAmount = (value) => Number(value || 0).toFixed(2)

const CollectionRow = ({ label, value, bold = false }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      gap: 2,
      py: bold ? '3px' : '2px',
      pl: bold ? 0 : '2px',
    }}
  >
    <Typography
      component="span"
      sx={{
        fontWeight: bold ? 700 : 500,
        fontSize: bold ? '18px' : '16px',
        color: '#111',
        lineHeight: 1.35,
      }}
    >
      {label}
    </Typography>
    <Typography
      component="span"
      sx={{
        fontWeight: bold ? 700 : 500,
        fontSize: bold ? '18px' : '16px',
        color: '#111',
        lineHeight: 1.35,
        whiteSpace: 'nowrap',
      }}
    >
      : {formatAmount(value)}
    </Typography>
  </Box>
)

const SectionDivider = () => <Box sx={{ borderTop: '1px solid #e0e0e0', my: '8px' }} />

const toAmount = (value) => Number(value) || 0

const mapCollectionRow = (row) => {
  const cashBooking = toAmount(row?.CashBooking)
  const qrBooking = toAmount(row?.QRBooking)
  const upiBooking = toAmount(row?.UPIBooking)
  const otherBooking = toAmount(row?.OtherBooking)
  const cashDelivery = toAmount(row?.CashDelivery)
  const qrDelivery = toAmount(row?.QRDelivery)
  const upiDelivery = toAmount(row?.UPIDelivery)
  const otherDelivery = toAmount(row?.OtherDelivery)
  const totalBooking = cashBooking + qrBooking + upiBooking + otherBooking
  const totalDelivery = cashDelivery + qrDelivery + upiDelivery + otherDelivery
  return {
    totalBooking,
    cashBooking,
    qrBooking,
    upiBooking,
    otherBooking,
    totalDelivery,
    cashDelivery,
    qrDelivery,
    upiDelivery,
    otherDelivery,
    totalCollection: totalBooking + totalDelivery,
  }
}

function UserCollectionPopup({ companyId, branchId, userId }) {
  const [anchorEl, setAnchorEl] = useState(null)
  const [data, setData] = useState(EMPTY_USER_COLLECTION)
  const open = Boolean(anchorEl)
  const canFetch = companyId != null && branchId != null && userId != null

  useEffect(() => {
    if (!open || !canFetch) return undefined
    let cancelled = false
    const today = moment().format('YYYY-MM-DD')

    const loadCollection = async () => {
      const res = await GetRequest(urls.getUserWiseCollection, {
        CompanyID: companyId,
        BranchID: branchId,
        UserID: userId,
        dtFrom: today,
        dtTo: today,
        PaymentTypeIDs: 0,
        Modes: 'B^D',
        PaymentModeIDs: 0,
      })
      if (cancelled) return
      const table5 = res?.status === 'Success' ? res?.Data?.Table5 : null
      const row = Array.isArray(table5) ? table5[0] : table5
      setData(row ? mapCollectionRow(row) : EMPTY_USER_COLLECTION)
    }

    loadCollection()
    return () => {
      cancelled = true
    }
  }, [open, canFetch, companyId, branchId, userId])

  const handleToggle = (event) => {
    setAnchorEl(open ? null : event.currentTarget)
  }

  return (
    <>
      <Button
        className="user-collection-btn"
        startIcon={<AccountBalanceWalletOutlinedIcon />}
        onClick={handleToggle}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        User Collection
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: '8px',
              width: 400,
              maxWidth: 'calc(100vw - 24px)',
              borderRadius: '14px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
              borderTop: '4px solid #0072D9',
              overflow: 'hidden',
            },
          },
        }}
      >
        <Box sx={{ px: '20px', py: '14px' }}>
          <CollectionRow label="Total Booking" value={data.totalBooking} bold />
          <CollectionRow label="Cash Booking" value={data.cashBooking} />
          <CollectionRow label="Qr Booking" value={data.qrBooking} />
          <CollectionRow label="UPI Booking" value={data.upiBooking} />
          <CollectionRow label="Credit/Debit Card & Other Bookings" value={data.otherBooking} />
          <SectionDivider />
          <CollectionRow label="Total Delivery" value={data.totalDelivery} bold />
          <CollectionRow label="Cash Delivery" value={data.cashDelivery} />
          <CollectionRow label="Qr Delivery" value={data.qrDelivery} />
          <CollectionRow label="UPI Delivery" value={data.upiDelivery} />
          <CollectionRow label="Credit/Debit Card & Other Delivery" value={data.otherDelivery} />
          <SectionDivider />
          <CollectionRow label="Total Collection" value={data.totalCollection} bold />
        </Box>
      </Popover>
    </>
  )
}

export default UserCollectionPopup
